import React, { useMemo, useState, useEffect } from "react"
import {
  Row,
  Container,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
} from "reactstrap";
import { ToastContainer, toast } from 'react-toastify';
import moment from 'moment';
import Breadcrumbs from "../../components/Common/Breadcrumb"
import TableContainer from "../../components/Common/TableContainer"
import { getIncidents } from "api/incidents"
import { useNavigate } from "react-router-dom"
import { exportExcel } from "api/crud"
import { IncidentReportSummary } from "api/reports"
import DatePicker from "react-datepicker"
import { useRole } from "hooks/useRole"
import Swal from "sweetalert2"

const IncidentIndex = () => {
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate()
  const dateFormat = 'DD-MM-YYYY';

  const { isAdmin, isSuperAdmin, isAnalyst } = useRole()

  React.useEffect(() => {
    document.title = "Incidents | CA Portal"
  }, [])

  const [incidentReportData, setIncidentReportData] = useState([])
  const [startDate, setStartDate] = useState(moment().startOf('month').format(dateFormat))
  const [endDate, setEndDate] = useState(moment(new Date()).format(dateFormat))
  const [incidentsData, setIncidentsData] = useState([])
  const [pages, setPages] = useState(0)

  const [otherParams, setOtherParams] = useState({
    pageSize: 10,
    pageNumber: 0,
    searchColumns: [],
    sortBy: "id",
    ascendingSort: true,
  })

  useEffect(() => {
    setLoading(true)
    try {
      fetchIncidentData({
        fromDate: startDate,
        toDate: endDate,
        ...otherParams,
      })
      fetchReportData({
        fromDate: startDate,
        toDate: endDate,
        ...otherParams,
      })
    } finally {
      setLoading(false);
    }

  }, [])

  const fetchIncidentData = async params => {
    try {
      const incidentRes = await getIncidents(params)

      setIncidentsData(incidentRes?.data)
      setPages(incidentRes?.totalPages)
      console.log(incidentRes.totalPages)
    } catch (error) {
      console.error("Error fetching incidents data:", error)
    }
  }

  const fetchReportData = async params => {
    try {
      const reportRes = await IncidentReportSummary(params)

      setIncidentReportData(reportRes.data)
    } catch (error) {
      console.error("Error fetching report data:", error)
    }
  }

  const handleApply = async () => {
    setLoading(true);

    try {
      const params = {
        fromDate: startDate,
        toDate: endDate,
        ...otherParams,
      };
      await Promise.all([
        fetchIncidentData(params),
        fetchReportData(params),
      ]);

    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setLoading(false);
    }
  }

  const handleStartDateChange = date => {
    setStartDate(moment(date).format(dateFormat))
  }

  const handleEndDateChange = date => {
    setEndDate(moment(date).format(dateFormat))
  }

  const columns = useMemo(
    () => [
      {
        Header: "#ID",
        accessor: "id",
      },
      {
        Header: "REPORT TYPE",
        accessor: "reportType",
      },

      {
        Header: "Reporter Name",
        accessor: row => {
          const firstName = row.reporter?.firstName || ""
          const lastName = row.reporter?.lastName || ""
          return firstName && lastName ? `${firstName} ${lastName}` : ""
        },
        Cell: ({ value }) => value,
      },

      {
        Header: "Reporter Email",
        accessor: row => row.reporter?.email || "",
        Cell: ({ value }) => value,
      },

      {
        Header: "STATUS",
        accessor: "status",
      },
      {
        Header: "DESCRIPTION",
        accessor: "description",
      },
      {
        Header: "DATE REPORTED",
        accessor: "createdAt",
        Cell: ({ value }) => {
          const formattedDate = new Date(value).toLocaleString(undefined, {
            month: "numeric",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          })
          return formattedDate
        },
      },
      {
        Header: "TICKET NUMBER",
        accessor: "ticket.ticketNumber",
      },
    ],
    []
  )

  const handleViewDetails = record => {
    navigate(`/incidents/${record.original.id}`)
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const params = {
        fromDate: startDate,
        toDate: endDate,
        ...otherParams,
      }

      await exportExcel("incidents", params)
    } catch (error) {
      toast.error("Failed to export incidents!");
      console.error("Error exporting incidents:", error)
    } finally {
      setExporting(false)
    }
  }

  const totalIncidents = incidentReportData
    ? incidentReportData.reduce((accumulator, monthData) => {
      return accumulator + monthData.totalIncidents
    }, 0)
    : 0

  const totalNewIncidents = incidentReportData
    ? incidentReportData.reduce((accumulator, monthData) => {
      return accumulator + monthData.totalNewIncidents
    }, 0)
    : 0

  const totalPendingIncidents = incidentReportData
    ? incidentReportData.reduce((accumulator, monthData) => {
      return accumulator + monthData.totalPendingIncidents
    }, 0)
    : 0

  const totalClosedIncidents = incidentReportData
    ? incidentReportData.reduce((accumulator, monthData) => {
      return accumulator + monthData.totalClosedIncidents
    }, 0)
    : 0

  const totalIdentifiedIncidents = incidentReportData
    ? incidentReportData.reduce((accumulator, monthData) => {
      return accumulator + monthData.totalIdentifiedIncidents
    }, 0)
    : 0

  const totalAnonymousIncidents = incidentReportData
    ? incidentReportData.reduce((accumulator, monthData) => {
      return accumulator + monthData.totalAnonymousIncidents
    }, 0)
    : 0;


  const getValidDate = (dateValue) => {
    if (!dateValue) return null;

    try {
      const date = new Date(dateValue);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    } catch (error) {
      return null;
    }
  };

  const onChangePagination = (paginationData) => {
    console.log("Pagination changed:", paginationData);
    setOtherParams(prevParams => ({
      ...prevParams,
      pageSize: paginationData.limit || prevParams.pageSize,
      pageNumber: paginationData.page || prevParams.pageNumber,
    }));
    fetchIncidentData({
      fromDate: startDate,
      toDate: endDate,
      ...otherParams,
      pageSize: paginationData.limit || otherParams.pageSize,
      pageNumber: paginationData.page || otherParams.pageNumber,
    });
  }

    
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Incidents" breadcrumbItem="Incident Reports" />
          <Row className="mb-3">
            <Col md={3}>
              <div className="d-flex flex-column align-items-center">
                <label className="form-label">Start Date</label>
                <DatePicker
                  selected={getValidDate(startDate)}
                  onChange={handleStartDateChange}
                  className="form-control"
                />
              </div>
            </Col>
            <Col md={3}>
              <div className="d-flex flex-column align-items-center">
                <label className="form-label">End Date</label>
                <DatePicker
                  selected={getValidDate(endDate)}
                  onChange={handleEndDateChange}
                  className="form-control"
                />
              </div>
            </Col>

            <Col md={3} className="d-flex">
              <Button
                color="primary"
                className="me-2 flex-grow-1"
                onClick={handleApply}
              >
                Apply Filter
              </Button>
              <Button
                color="success"
                onClick={handleExport}
                className="me-2 flex-grow-1"
              >
                Export Incidents
              </Button>
            </Col>
            {(isAdmin || isSuperAdmin || isAnalyst) && (
              <Col md={3}>
                <div className="d-flex flex-column align-items-center mt-3">
                  <Button disabled={exporting} color="success" onClick={handleExport}>
                    Export Incidents
                  </Button>
                </div>
              </Col>
            )}
          </Row>

          <Row className="mb-4">
            <Col md={4}>
              <Card>
                <CardBody>
                  <h5 className="card-title">Total Incidents</h5>
                  <p>{totalIncidents}</p>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody>
                  <h5 className="card-title">Total New Incidents</h5>
                  <p>{totalNewIncidents}</p>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody>
                  <h5 className="card-title">Total Pending Incidents</h5>
                  <p>{totalPendingIncidents}</p>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody>
                  <h5 className="card-title">Total Closed Incidents</h5>
                  <p>{totalClosedIncidents}</p>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody>
                  <h5 className="card-title">Total Identified Incidents</h5>
                  <p>{totalIdentifiedIncidents}</p>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody>
                  <h5 className="card-title">Total Anonymous Incidents</h5>
                  <p>{totalAnonymousIncidents}</p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <TableContainer
            showView={isAdmin || isSuperAdmin || isAnalyst}
            columns={columns}
            data={incidentsData || []}
            isGlobalFilter={true}
            loading={loading}
            isAddOptions={false}
            handleView={handleViewDetails}
            customPageSize={10}
            isPagination={true}
            pages={pages}
            iscustomPageSizeOptions={true}
            tableClass="align-middle table-nowrap table-check table"
            theadClass="table-light"
            paginationDiv="col-12"
            pagination="justify-content-right pagination pagination-rounded"
            paginationData={otherParams}
            onChangePagination={onChangePagination}
          />
        </Container>
      </div>
    </React.Fragment>
  )
}

export default IncidentIndex
